import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { 
  PublicClientApplication, 
  Configuration, 
  AccountInfo, 
  AuthenticationResult,
  InteractionRequiredAuthError,
  SilentRequest
} from '@azure/msal-browser'
import { MsalProvider } from '@azure/msal-react'

// MSAL configuration
const msalConfig: Configuration = {
  auth: {
    clientId: import.meta.env.VITE_AZURE_CLIENT_ID || '',
    authority: `https://login.microsoftonline.com/${import.meta.env.VITE_AZURE_TENANT_ID || 'common'}`,
    redirectUri: import.meta.env.VITE_AZURE_REDIRECT_URI || window.location.origin,
    postLogoutRedirectUri: window.location.origin
  },
  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: false
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return
        switch (level) {
          case 1: // Error
            console.error('[MSAL]', message)
            break
          case 2: // Warning
            console.warn('[MSAL]', message)
            break
          case 3: // Info
            console.info('[MSAL]', message)
            break
          case 4: // Verbose
            console.debug('[MSAL]', message)
            break
        }
      }
    }
  }
}

// Create MSAL instance
const msalInstance = new PublicClientApplication(msalConfig)

// Login request configuration
const loginRequest = {
  scopes: [
    'User.Read',
    'GroupMember.Read.All'
  ],
  prompt: 'select_account' as const
}

// Silent request configuration for token refresh
const silentRequest: SilentRequest = {
  scopes: loginRequest.scopes,
  account: undefined
}

// User permissions interface
export interface UserPermissions {
  isAdmin: boolean
  isWriter: boolean
  isReader: boolean
  groups: string[]
}

// Auth context interface
export interface AuthContextType {
  isAuthenticated: boolean
  isLoading: boolean
  user: AccountInfo | null
  userPermissions: UserPermissions | null
  accessToken: string | null
  login: () => Promise<void>
  logout: () => Promise<void>
  getAccessToken: () => Promise<string | null>
}

// Create auth context
export const AuthContext = createContext<AuthContextType | null>(null)

// Auth provider props
interface AuthProviderProps {
  children: ReactNode
}

// Auth provider component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [user, setUser] = useState<AccountInfo | null>(null)
  const [userPermissions, setUserPermissions] = useState<UserPermissions | null>(null)
  const [accessToken, setAccessToken] = useState<string | null>(null)

  // Initialize MSAL and check for existing authentication
  useEffect(() => {
    const initializeMsal = async () => {
      try {
        await msalInstance.initialize()
        
        // Handle redirect response
        const response = await msalInstance.handleRedirectPromise()
        if (response) {
          await handleAuthenticationResult(response)
        } else {
          // Check if user is already logged in
          const accounts = msalInstance.getAllAccounts()
          if (accounts.length > 0) {
            const account = accounts[0]
            setUser(account)
            setIsAuthenticated(true)
            
            // Get access token silently
            try {
              silentRequest.account = account
              const tokenResponse = await msalInstance.acquireTokenSilent(silentRequest)
              setAccessToken(tokenResponse.accessToken)
              await fetchUserPermissions(tokenResponse.accessToken)
            } catch (error) {
              console.warn('Silent token acquisition failed:', error)
              // Token is expired - clear all authentication state and force re-login
              setIsAuthenticated(false)
              setUser(null)
              setUserPermissions(null)
              setAccessToken(null)
              // Clear MSAL cache to ensure clean state
              await msalInstance.clearCache()
            }
          }
        }
      } catch (error) {
        console.error('MSAL initialization failed:', error)
      } finally {
        setIsLoading(false)
      }
    }

    initializeMsal()
  }, [])

  // Periodic token validation to detect expired sessions
  useEffect(() => {
    if (!isAuthenticated || !user) return

    const validateToken = async () => {
      try {
        // Try to get a fresh token silently
        silentRequest.account = user
        const tokenResponse = await msalInstance.acquireTokenSilent(silentRequest)
        
        // Validate the token with the backend
        const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/user/info`, {
          headers: {
            'Authorization': `Bearer ${tokenResponse.accessToken}`,
            'Content-Type': 'application/json'
          }
        })

        if (!response.ok) {
          console.warn('Token validation failed - forcing re-authentication')
          setIsAuthenticated(false)
          setUser(null)
          setUserPermissions(null)
          setAccessToken(null)
          await msalInstance.clearCache()
        }
      } catch (error) {
        console.warn('Token validation error - forcing re-authentication:', error)
        setIsAuthenticated(false)
        setUser(null)
        setUserPermissions(null)
        setAccessToken(null)
        await msalInstance.clearCache()
      }
    }

    // Validate token every 5 minutes
    const interval = setInterval(validateToken, 5 * 60 * 1000)
    
    return () => clearInterval(interval)
  }, [isAuthenticated, user])

  // Handle authentication result
  const handleAuthenticationResult = async (result: AuthenticationResult) => {
    if (result.account) {
      setUser(result.account)
      setIsAuthenticated(true)
      setAccessToken(result.accessToken)
      await fetchUserPermissions(result.accessToken)
    }
  }

  // Fetch user permissions from backend
  const fetchUserPermissions = async (token: string) => {
    console.log('🔄 Fetching user permissions...', {
      apiUrl: `${import.meta.env.VITE_API_BASE_URL}/api/v1/user/info`,
      hasToken: !!token,
      tokenPreview: token ? `${token.substring(0, 50)}...` : 'No token'
    })
    
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/user/info`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      console.log('📡 API Response:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok
      })

      if (response.ok) {
        const userInfo = await response.json()
        console.log('📋 User Info from API:', userInfo)
        
        // Use the permissions object from the backend response
        const backendPermissions = userInfo.permissions || {}
        const groups = userInfo.groups || []
        
        console.log('🔍 Permission Debug:', {
          backendPermissions,
          userGroups: groups,
          finalPermissions: {
            isAdmin: backendPermissions.is_admin || false,
            isWriter: backendPermissions.can_write || false,
            isReader: backendPermissions.can_read || false
          }
        })
        
        const permissions: UserPermissions = {
          isAdmin: backendPermissions.is_admin || false,
          isWriter: backendPermissions.can_write || false,
          isReader: backendPermissions.can_read || false,
          groups: groups
        }
        setUserPermissions(permissions)
      } else {
        console.error('Failed to fetch user permissions:', response.statusText)
        
        // If token is invalid/expired (401), force re-authentication
        if (response.status === 401) {
          console.warn('Token expired or invalid - forcing re-authentication')
          setIsAuthenticated(false)
          setUser(null)
          setUserPermissions(null)
          setAccessToken(null)
          // Clear MSAL cache to ensure clean state
          await msalInstance.clearCache()
          return
        }
        
        // For other errors, set default permissions
        setUserPermissions({
          isAdmin: false,
          isWriter: false,
          isReader: true,
          groups: []
        })
      }
    } catch (error) {
      console.error('Error fetching user permissions:', error)
      
      // If it's a network error or token-related error, force re-authentication
      if (error instanceof TypeError && error.message.includes('fetch')) {
        console.warn('Network error fetching permissions - may indicate token issues')
      }
      
      // For any error fetching permissions, force re-authentication to be safe
      console.warn('Permission fetch failed - forcing re-authentication for security')
      setIsAuthenticated(false)
      setUser(null)
      setUserPermissions(null)
      setAccessToken(null)
      // Clear MSAL cache to ensure clean state
      await msalInstance.clearCache()
    }
  }

  // Login function
  const login = async () => {
    try {
      setIsLoading(true)
      // Use redirect instead of popup for better compatibility with SPA
      await msalInstance.loginRedirect(loginRequest)
    } catch (error) {
      console.error('Login failed:', error)
      setIsLoading(false)
      throw error
    }
    // Note: setIsLoading(false) will be called in handleRedirectPromise after redirect
  }

  // Logout function
  const logout = async () => {
    try {
      setIsLoading(true)
      const account = msalInstance.getAllAccounts()[0]
      if (account) {
        await msalInstance.logoutRedirect({
          account,
          postLogoutRedirectUri: window.location.origin
        })
      }
      
      // Clear state
      setIsAuthenticated(false)
      setUser(null)
      setUserPermissions(null)
      setAccessToken(null)
    } catch (error) {
      console.error('Logout failed:', error)
      // Even if logout fails, clear local state
      setIsAuthenticated(false)
      setUser(null)
      setUserPermissions(null)
      setAccessToken(null)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  // Get access token (with automatic refresh)
  const getAccessToken = async (): Promise<string | null> => {
    if (!user) return null

    try {
      silentRequest.account = user
      const response = await msalInstance.acquireTokenSilent(silentRequest)
      setAccessToken(response.accessToken)
      return response.accessToken
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        // Token expired or interaction required, try popup
        try {
          const response = await msalInstance.acquireTokenPopup(loginRequest)
          setAccessToken(response.accessToken)
          return response.accessToken
        } catch (popupError) {
          console.error('Token acquisition failed:', popupError)
          // Force re-login and clear all state
          setIsAuthenticated(false)
          setUser(null)
          setUserPermissions(null)
          setAccessToken(null)
          // Clear MSAL cache to ensure clean state
          await msalInstance.clearCache()
          return null
        }
      } else {
        console.error('Token acquisition failed:', error)
        // Force re-authentication on any token acquisition failure
        setIsAuthenticated(false)
        setUser(null)
        setUserPermissions(null)
        setAccessToken(null)
        // Clear MSAL cache to ensure clean state
        await msalInstance.clearCache()
        return null
      }
    }
  }

  // Context value
  const contextValue: AuthContextType = {
    isAuthenticated,
    isLoading,
    user,
    userPermissions,
    accessToken,
    login,
    logout,
    getAccessToken
  }

  return (
    <MsalProvider instance={msalInstance}>
      <AuthContext.Provider value={contextValue}>
        {children}
      </AuthContext.Provider>
    </MsalProvider>
  )
}

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

// HOC for protected routes
export const withAuth = <P extends object>(
  Component: React.ComponentType<P>,
  requiredPermission?: 'admin' | 'writer' | 'reader'
) => {
  return (props: P) => {
    const { isAuthenticated, isLoading, userPermissions } = useAuth()

    if (isLoading) {
      return <div>Loading...</div> // You can replace with a proper loading component
    }

    if (!isAuthenticated) {
      return <div>Please log in to access this page.</div>
    }

    // Check permissions if required
    if (requiredPermission && userPermissions) {
      const hasPermission = 
        (requiredPermission === 'admin' && userPermissions.isAdmin) ||
        (requiredPermission === 'writer' && (userPermissions.isWriter || userPermissions.isAdmin)) ||
        (requiredPermission === 'reader' && (userPermissions.isReader || userPermissions.isWriter || userPermissions.isAdmin))

      if (!hasPermission) {
        return <div>You don't have permission to access this page.</div>
      }
    }

    return <Component {...props} />
  }
}

export default AuthProvider
